import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../../../../config";

const BlogLists = () => {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const loaderRef = useRef(null);
  const navigate = useNavigate();

  const handleViewDetails = (blog) => {
    navigate("/blog-details", { state: { blog } });
  };
  // Fetch paginated blogs from backend
  const fetchBlogs = useCallback(async (currentPage) => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${API_URL}/layout/blogs?page=${currentPage}&limit=6`
      );
      const newBlogs = response?.data?.data || [];

      setBlogs((prevBlogs) => [
        ...prevBlogs,
        ...newBlogs.filter(
          (newBlog) => !prevBlogs.some((blog) => blog.id === newBlog.id)
        ),
      ]);

      setHasMore(response?.data?.hasMore || false);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching blog data:", error);
      setIsLoading(false);
    }
  }, []);

  // Load initial data
  useEffect(() => {
    fetchBlogs(page);
  }, [page, fetchBlogs]);

  // Load more blogs when the user scrolls
  const loadMoreBlogs = useCallback(() => {
    if (hasMore && !isLoading) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [hasMore, isLoading]);

  // IntersectionObserver to trigger load more
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMoreBlogs();
        }
      },
      { threshold: 0.5 }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [loadMoreBlogs, hasMore, isLoading]);

  return (
    <>
      <section className="mt-4">
        <div className="container">
          <div className="cs_section_heading cs_style_1 text-center">
            <h2 className="cs_section_title mb-3">Latest Posts & Articles</h2>
          </div>
          <div className="cs_posts_grid cs_style_1">
            {blogs.map((blog) => (
              <article
                key={blog.id}
                className="cs_post cs_style_1"
                style={{ height: "100%" }}
              >
                <div
                  className="cs_post_wrapper"
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <a
                    href={blog.detailsUrl}
                    className="cs_post_thumbnail position-relative"
                    style={{ height: "250px", overflow: "hidden" }}
                  >
                    <img
                      src={blog?.thumbnail}
                      alt="Post Thumbnail"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </a>
                  <div
                    className="cs_post_content position-relative"
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <h3 className="cs_post_title">
                      <a href={blog.detailsUrl}>{blog.title}</a>
                    </h3>
                    <div style={{ marginTop: "auto" }}>
                      <div
                        onClick={() => handleViewDetails(blog)}
                        className="cs_post_btn"
                        style={{ cursor: "pointer" }}
                      >
                        <span>Read More</span>
                        <span>
                          <i className="fa-solid fa-angle-right"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Skeleton Loader */}
          {isLoading && (
            <div className="skeleton-container">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="skeleton-card">
                  <div className="skeleton-wrapper">
                    <div className="skeleton-thumbnail"></div>
                    <div className="skeleton-content">
                      <div className="skeleton-title"></div>
                      <div className="skeleton-meta"></div>
                      <div className="skeleton-button"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Loader for Infinite Scroll */}
          <div ref={loaderRef} className="text-center my-4">
            {hasMore && !isLoading && (
              <button className="cs_load_more_btn" onClick={loadMoreBlogs}>
                Load More
              </button>
            )}
          </div>
        </div>
        <div className="cs_height_120 cs_height_lg_80"></div>
      </section>
    </>
  );
};

export default BlogLists;
